/**
 * WorkspacePage — report workspace with integrated navigation + viewer.
 *
 * Layout: WorkspaceNav (collapsible left) | ReportViewer (flex:1). Below desktop
 * the nav overlays the viewer instead and closes itself on file select.
 * Selected file state lives in WorkspaceNavContext so it persists
 * when navigating to other pages (Apps, Tasks) and back.
 *
 * URL deep-link: if ?workspace=<path> is present in the URL,
 * auto-navigates to the specified file (used for links from chat or email).
 * Handles both direct page load and post-auth redirect scenarios.
 */

import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import WorkspaceNav from "../../components/WorkspaceNav/WorkspaceNav";
import ReportViewer from "../../components/ReportViewer/ReportViewer";
import { WorkspaceFile } from "../../client/GatewayClient";
import { useWorkspaceNav } from "../../context/WorkspaceNavContext";
import { useRecentFiles } from "../../hooks/useRecentFiles";
import { isCompact, useBreakpoint } from "../../hooks/useBreakpoint";
import "./WorkspacePage.css";

export default function WorkspacePage() {
  const { navCollapsed, toggleNav, setNavCollapsed, selectedFile, setSelectedFile, refreshWorkspace, setPendingNavigateKey } = useWorkspaceNav();
  const { recentFiles, recordOpen, removeRecent } = useRecentFiles();
  const location = useLocation();

  // Below desktop the tree is an overlay on top of the report (WorkspaceNav.css),
  // not a column beside it. So: start closed — the landing is WorkspaceHome, not a
  // file list covering it — and close it once a file is picked, or the user would
  // have to dismiss it by hand to see what they just opened.
  const compact = isCompact(useBreakpoint());
  useEffect(() => {
    if (compact) setNavCollapsed(true);
  }, [compact, setNavCollapsed]);

  const handleFileSelect = useCallback(
    (file: WorkspaceFile) => {
      setSelectedFile(file);
      recordOpen(file);
      if (compact) setNavCollapsed(true);
    },
    [setSelectedFile, recordOpen, compact, setNavCollapsed],
  );

  // ── Handle ?workspace= URL deep-link ──────────────────────────────────
  // On first mount WorkspaceNav already calls loadAll(), so we only set
  // pendingNavigateKey and let that initial fetch satisfy the request.
  // On subsequent location.search changes (e.g. navigating from chat while
  // already on WorkspacePage) we call refreshWorkspace() to re-fetch.
  const isMountRef = useRef(true);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wsPath = params.get('workspace');
    if (wsPath) {
      // Only refresh if already mounted; the initial fetch in WorkspaceNav suffices
      if (!isMountRef.current) {
        refreshWorkspace();
      }
      setPendingNavigateKey(wsPath);
      window.history.replaceState(null, '', location.pathname);
    }
    isMountRef.current = false;
  }, [location.search, refreshWorkspace, setPendingNavigateKey, location.pathname]);

  return (
    <div className="workspace">
      <WorkspaceNav
        onFileSelect={handleFileSelect}
        selectedFileKey={selectedFile?.key}
        isCollapsed={navCollapsed}
        onToggle={toggleNav}
        recentFiles={recentFiles}
        onRemoveRecent={removeRecent}
      />
      {compact && !navCollapsed && (
        <div className="workspace__nav-backdrop" onClick={toggleNav} aria-hidden="true" />
      )}
      <ReportViewer file={selectedFile} onFileNotFound={removeRecent} />
    </div>
  );
}
